import { useEffect, useState } from "react";
import { Percent } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getAdminDiscounts, setAdminDiscount } from "@/lib/api";

const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const fmtEnd = (iso) => new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

const DiscountRow = ({ item, onSaved }) => {
  const [pct, setPct] = useState(String(item.active ? item.percent : ""));
  const [end, setEnd] = useState(toLocalInput(item.active ? item.ends_at : ""));
  const preview = Number(pct) > 0 ? item.price * (1 - Number(pct) / 100) : item.price;

  const save = async (percent, endsAt) => {
    try {
      await setAdminDiscount(item.id, { percent, ends_at: endsAt });
      onSaved();
      toast.success(percent > 0 ? `${item.name}: ${percent}% off saved` : `${item.name}: discount removed`);
    } catch (err) {
      toast.error(err?.response?.data?.detail && typeof err.response.data.detail === "string" ? err.response.data.detail : "Could not save discount");
    }
  };

  const submit = () => {
    const p = Math.min(90, parseInt(pct, 10) || 0);
    if (p > 0 && !end) { toast.error("Please pick an end date and time"); return; }
    save(p, p > 0 ? new Date(end).toISOString() : null);
  };

  const remove = () => { setPct(""); setEnd(""); save(0, null); };

  return (
    <div data-testid={`discount-row-${item.id}`} className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-100 p-3">
      <img src={item.image} alt="" className="h-10 w-10 rounded-md object-cover" />
      <div className="min-w-[140px] flex-1">
        <p className="truncate text-sm font-medium text-slate-900">{item.name}</p>
        <p data-testid={`discount-status-${item.id}`} className={`text-xs ${item.active ? "text-emerald-600" : "text-slate-500"}`}>
          {item.active ? `${item.percent}% off · ${gbp(item.sale_price)} until ${fmtEnd(item.ends_at)}` : `Full price · ${gbp(item.price)}`}
        </p>
      </div>
      <div className="relative">
        <Input data-testid={`discount-percent-${item.id}`} value={pct} onChange={(e) => setPct(String(Math.min(90, Number(e.target.value.replace(/\D/g, "").slice(0, 2)) || 0) || ""))} placeholder="0" inputMode="numeric" className="h-8 w-16 border-slate-200 bg-white pr-6 text-center" />
        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
      </div>
      <Input data-testid={`discount-end-${item.id}`} type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} className="h-8 w-[200px] border-slate-200 bg-white text-sm" />
      <span className="w-16 text-right text-sm font-semibold text-slate-900">{gbp(preview)}</span>
      <Button size="sm" onClick={submit} data-testid={`discount-save-${item.id}`} className="h-8 bg-blue-600 text-white hover:bg-blue-700">Save</Button>
      {item.active && <Button size="sm" variant="ghost" onClick={remove} data-testid={`discount-remove-${item.id}`} className="h-8 text-rose-600 hover:bg-rose-50">Remove</Button>}
    </div>
  );
};

export const DiscountPanel = () => {
  const [items, setItems] = useState([]);
  const load = () => getAdminDiscounts().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <section data-testid="discount-panel" className="mt-6 rounded-xl bg-white p-5 ring-1 ring-slate-100">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Percent className="h-4 w-4 text-blue-600" />
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Discounts</p>
        <span className="text-xs text-slate-400">· set % off (max 90) and when it ends (UK time) — it switches off by itself</span>
      </div>
      <div className="grid gap-2">
        {items.map((it) => <DiscountRow key={`${it.id}-${it.percent}-${it.ends_at}`} item={it} onSaved={load} />)}
      </div>
    </section>
  );
};

export default DiscountPanel;
